import Konva from 'konva';
import { watch } from 'vue';

import HaspObject from './HaspObject';
import { useImageStore } from '../stores/imageStore.js';
import { reportImportIssue } from '../utils/importIssues';

/**
 * A lightweight consumer of ImageResource (see src/stores/imageStore.js).
 * This class never loads, decodes, or caches image bytes, and never talks
 * to IndexedDB — it resolves a shared ImageResource, observes it reactively,
 * and points its own Konva.Image at whatever bitmap the resource owns.
 * Mirrors the firmware's Image Resource Manager: the resource owns the
 * decoded pixel data, widgets only reference it (see
 * docs/FIRMWARE-IMAGE-RESOURCE-MANAGER-ANALYSIS.md).
 */
export default class HaspImage extends HaspObject {
    imageNode; // per-widget Konva.Image — position/size/rotation are legitimately per-instance
    resource; // the shared ImageResource this widget currently references, or null
    zoom = 1;
    initialImageSize;
    imageLocal = false;
    pivotMoved = false;
    loading = false;

    constructor(config) {
        config.zoom ??= 256;
        const pad = Math.max(config.pad_top, config.pad_bottom, config.pad_left, config.pad_right);
        if (!isNaN(pad)) config.padding = pad;
        super(config);

        this.zoom = config.zoom / 256;
        this.imageNode = new Konva.Image({ image: null });

        // Primary reference is the ImageResource uuid (`this.image`, set by the
        // generic schema property assignment above). Designs saved before this
        // field existed only have `src`/`filename` — resolve those and backfill
        // `this.image` once found, so the next save carries the uuid forward.
        if (this.image) {
            this._attachToUuid(this.image);
        } else {
            this._attachToSrc(config.src);
        }

        this.initialImagePos = { x: this.x(), y: this.y() };

        this.on('transform', () => {
            this.hasp_angle = this.rotation();
            this.zoom = this.height() / this.initialImageSize.height;
            this.applyZoomFactor(this.imageNode);
        });

        this.on('transformend', () => {
            this.hasp_angle = this.rotation();
            this.zoom = this.height() / this.initialImageSize.height;
            this.applyZoomFactor(this.imageNode);
        });

        this.on('angle', () => {
            this.rotation(this.hasp_angle);
        });

        // Fired by PropertiesPanel when the user picks a different library image.
        this.on('image', () => this._attachToUuid(this.image));

        // Fired when the user edits the `src` text field directly (a raw
        // remote URL or desktop-local path, unrelated to the image library).
        this.on('reload-image', () => this._attachToSrc(this.src));

        this.on('destroy', () => {
            this._stopResourceWatch?.();
            this._stopImageWatch?.();
            this._stopErrorWatch?.();
            // A stopped Vue watcher can already have a queued callback. Clear
            // the handles as well so those callbacks fail the same cleanup
            // check as callbacks from a replaced resource.
            this._stopResourceWatch = undefined;
            this._stopImageWatch = undefined;
            this._stopErrorWatch = undefined;
        });

        this.setFill('rgba(0,255,0,0.0');
    }

    /** Primary resolution path: look up the shared ImageResource by uuid. */
    _attachToUuid(uuid) {
        this._stopResourceWatch?.();
        this._stopResourceWatch = undefined;
        const imageStore = useImageStore();
        imageStore.ensureLoaded().catch((error) => {
            reportImportIssue('error', 'Failed to load the image library', error?.message ?? String(error));
        });

        let stopResourceWatch;
        stopResourceWatch = watch(
            () => [imageStore.getResourceByUuid(uuid), imageStore.loaded],
            ([resource, loaded]) => {
                if (stopResourceWatch && this._stopResourceWatch !== stopResourceWatch) return;
                if (this.image !== uuid) return;
                if (resource) {
                    this._attachResource(resource);
                } else if (loaded) {
                    reportImportIssue('warning', `Image not found: ${this.filename || uuid}`, 'Upload it via the Image Store to restore it.');
                    this.idTextObj?.moveToTop();
                }
            },
            { immediate: true },
        );
        this._stopResourceWatch = stopResourceWatch;
    }

    /**
     * Backward-compatibility resolution path for designs saved before `image`
     * (uuid) existed: derive a filename from `src` and look that up instead.
     * Backfills `this.image` once a matching resource is found.
     */
    _attachToSrc(src) {
        this._stopResourceWatch?.();
        this._stopResourceWatch = undefined;
        const filename = src.split('/').pop();
        const imageStore = useImageStore();
        imageStore.ensureLoaded().catch((error) => {
            reportImportIssue('error', `Failed to load image: ${filename}`, error?.message ?? String(error));
        });

        let stopResourceWatch;
        stopResourceWatch = watch(
            () => [imageStore.getResourceByFilename(filename), imageStore.loaded],
            ([resource, loaded]) => {
                if (stopResourceWatch && this._stopResourceWatch !== stopResourceWatch) return;
                if (this.src !== src) return;
                if (resource) {
                    this.image = resource.uuid; // backfill the primary reference for future saves
                    this._attachResource(resource);
                } else if (loaded) {
                    if (src.startsWith('L:') || src.startsWith('file://')) {
                        // L:/ and file:// paths are device-local and cause uncatchable CORS errors.
                        reportImportIssue('warning', `Image not found: ${filename}`, 'Upload it via the Image Store to restore it.');
                        this.idTextObj?.moveToTop();
                    } else {
                        // Not a library resource (remote URL, or the default placeholder
                        // path) — still routed through ImageResource, just an
                        // unshared/unpersisted one (see acquireExternalResource).
                        this._attachResource(imageStore.acquireExternalResource(src, filename));
                    }
                }
            },
            { immediate: true },
        );
        this._stopResourceWatch = stopResourceWatch;
    }

    /**
     * Point this widget at a resolved ImageResource — the single place that
     * observes a resource's decode state and reacts to it. Used for every
     * kind of resource (library, or the unshared external wrapper), so
     * there is exactly one "consume a resource" code path regardless of how
     * it was resolved.
     */
    _attachResource(resource) {
        this._stopImageWatch?.();
        this._stopErrorWatch?.();
        this._stopImageWatch = undefined;
        this._stopErrorWatch = undefined;
        this.resource = resource;
        this.filename = resource.filename; // kept in sync for objectExport()/back-compat readers
        this.src = resource.src;

        resource.ensureDecoded();

        let stopImageWatch;
        stopImageWatch = watch(
            () => resource.image,
            (image) => {
                if (stopImageWatch && this._stopImageWatch !== stopImageWatch) return;
                if (this.resource !== resource) return;
                if (image) this._onImageReady(image);
            },
            { immediate: true },
        );
        this._stopImageWatch = stopImageWatch;

        let stopErrorWatch;
        stopErrorWatch = watch(
            () => resource.error,
            (error) => {
                if (stopErrorWatch && this._stopErrorWatch !== stopErrorWatch) return;
                if (this.resource !== resource) return;
                if (error) reportImportIssue('error', `Failed to load image: ${resource.filename}`, error);
            },
            { immediate: true },
        );
        this._stopErrorWatch = stopErrorWatch;
    }

    _onImageReady(imageEl) {
        // A previous resource may finish decoding after the user has already
        // selected another one. Its dimensions must never win the race.
        if (this.resource == null) return;

        const width = Number(imageEl?.naturalWidth ?? imageEl?.width ?? 0);
        const height = Number(imageEl?.naturalHeight ?? imageEl?.height ?? 0);
        if (width <= 0 || height <= 0) return;

        if (this.imageNode.image() !== imageEl) {
            this.imageNode.image(imageEl);
        }
        this.add(this.imageNode);
        this.initialImageSize = { width, height };

        // Selecting a resource is an intrinsic-size operation. Reset the
        // previous per-object zoom and set the image node's dimensions before
        // going through setNewSize(), so the Designer model, rect and bitmap
        // all receive the same geometry. Manual Transformer resizes still use
        // setNewSize() and therefore retain their existing stretch rules.
        this.zoom = 1;
        this.imageNode.size({ width, height });
        this.setNewSize({ width, height });

        this.idTextObj.moveToTop();

        const layer = this.getLayer?.();
        const selected = layer?.haspobject === this || layer?.selectedObjects?.includes(this);
        if (selected) {
            layer.updateTransformer?.();
            layer.transformer?.forceUpdate?.();
        }
        layer?.batchDraw?.();
        layer?.layerCallback?.('image-loaded', this);
    }

    getBinaryImageData() {
        const base64String = this.imageNode.image().src.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');

        // Decode the Base64 string
        const binaryString = atob(base64String);

        // Convert to binary data
        const binaryLength = binaryString.length;
        const binaryData = new Uint8Array(binaryLength);

        for (let i = 0; i < binaryLength; i++) {
            binaryData[i] = binaryString.charCodeAt(i);
        }

        return binaryData;
    }

    /**
     * Applies the zoom factor to the Konvaimage.
     * @param {*} image
     * @returns
     */
    setNewSize(size) {
        super.setNewSize(size);
        if (this.imageNode && this.initialImageSize) {
            this.hasp_angle = this.rotation();
            this.zoom = size.height / this.initialImageSize.height;
            this.applyZoomFactor(this.imageNode);
        }
    }

    applyZoomFactor(image) {
        if (this.zoom === 1) return;
        var imgSize = this.rect.size();
        image.size(imgSize);
    }

    setImageLocal(isLocal) {
        this.imageLocal = Boolean(isLocal);
    }

    objectExport(page, objectData) {
        // Written unconditionally, same as HaspChart/HaspLegend's `dataset`
        // uuid — design-save keeps it as-is so uuid round-trips across saves
        // (Exporter._stripImageUuids() removes it again for firmware-facing
        // plate JSONL, which has no concept of this Designer-internal field).
        if (this.image) {
            objectData.image = this.image;
        }

        // Prefer the currently-attached resource's filename (reflects a
        // library rename that happened after this widget was placed);
        // this.filename is the fallback for the rare case no resource is
        // currently attached (still resolving, or genuinely missing).
        const filename = this.resource?.filename || this.filename;

        if (filename) {
            objectData.src = 'L:/' + filename;
        } else if (this.imageLocal === false) {
            objectData.src = this.src.trim();
        } else {
            var index = this.src.lastIndexOf('/');
            var fn = this.src.substring(index);
            objectData.src = 'L:' + fn;
            objectData.comment = this.src.substring(0, index);
        }

        if (this.hasp_angle !== 0) {
            objectData.angle = this.hasp_angle * 10;
        }

        objectData.pivot_x = 0;
        objectData.pivot_y = 0;

        if (this.initialImageSize && this.width() !== this.initialImageSize.width) {
            objectData.zoom = (this.width() / this.initialImageSize.width) * 256;
        }
    }
}
